import React, { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { TERRITOIRES } from "../lib/api";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";
import { Button } from "../components/ui/button";
import {
  LayoutDashboard, Calculator, HardHat, FileText, Users, Truck, Wrench, Map as MapIcon,
  ShieldAlert, CheckSquare, Bot, DatabaseZap, Info, Menu, Sun, Moon, LogOut, Plus,
  Package, ShoppingCart,
} from "lucide-react";
import QuickNew from "./QuickNew";

const NAV = [
  { to: "/", label: "Tableau de bord", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/calculs", label: "Calculs", icon: Calculator, testid: "nav-calculs" },
  { to: "/chantiers", label: "Chantiers", icon: HardHat, testid: "nav-chantiers" },
  { to: "/devis", label: "Devis", icon: FileText, testid: "nav-devis" },
  { to: "/simulateur", label: "Simulateur commande", icon: ShoppingCart, testid: "nav-simulateur" },
  { to: "/crm", label: "CRM / Contacts", icon: Users, testid: "nav-crm" },
  { to: "/fournisseurs", label: "Fournisseurs & Prix", icon: Package, testid: "nav-fournisseurs" },
  { to: "/sous-traitants", label: "Sous-traitants", icon: Truck, testid: "nav-sous-traitants" },
  { to: "/materiel", label: "Matériel & Véhicules", icon: Wrench, testid: "nav-materiel" },
  { to: "/carte", label: "Cartographie", icon: MapIcon, testid: "nav-carte" },
  { to: "/risques", label: "Risques DROM", icon: ShieldAlert, testid: "nav-risques" },
  { to: "/taches", label: "Tâches", icon: CheckSquare, testid: "nav-taches" },
  { to: "/assistant", label: "TECA Assistant", icon: Bot, testid: "nav-assistant" },
  { to: "/sources", label: "Connecter mes sources", icon: DatabaseZap, testid: "nav-sources" },
  { to: "/infos", label: "Contact & Infos", icon: Info, testid: "nav-infos" },
];

export default function Layout() {
  const { user, logout, theme, setTheme, territoire, setTerritoire } = useApp();
  const [open, setOpen] = useState(false);
  const [quickNew, setQuickNew] = useState(false);
  const navigate = useNavigate();

  const linkCls = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-3.5 rounded-xl font-semibold text-sm transition-colors touch-target ${
      isActive ? "bg-amber-500 text-slate-900" : "text-slate-200 hover:bg-teca-800"}`;

  return (
    <div className="min-h-screen bg-background" data-testid="app-layout">
      <header className="sticky top-0 z-40 bg-teca-900 dark:bg-teca-950 text-white border-b-4 border-amber-500">
        <div className="flex items-center gap-3 px-4 py-2.5 max-w-7xl mx-auto">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="menu-drawer-button" className="text-white hover:bg-teca-800 touch-target">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-80 bg-teca-900 text-white border-r-slate-700 p-0 overflow-y-auto">
              <div className="p-5 border-b border-slate-700">
                <div className="font-display text-2xl font-bold tracking-tight uppercase">TECA <span className="text-amber-500">BUILD 360</span></div>
                <div className="text-xs text-slate-400 mt-1">Calculs • Chantiers • Devis • CRM • Matériel • Risques DROM</div>
                <div className="text-xs text-slate-500 mt-2">{user?.name} — {user?.role === "super_admin" ? "Super Admin" : "Utilisateur"}</div>
              </div>
              <nav className="p-3 space-y-1">
                {NAV.map((n) => (
                  <NavLink key={n.to} to={n.to} end={n.end} className={linkCls} onClick={() => setOpen(false)} data-testid={n.testid}>
                    <n.icon className="h-5 w-5" /> {n.label}
                  </NavLink>
                ))}
              </nav>
              <div className="p-4 border-t border-slate-700 space-y-3">
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">Territoire</div>
                  <div className="flex gap-1.5">
                    {TERRITOIRES.map((t) => (
                      <button key={t} data-testid={`territory-${t.toLowerCase()}`} onClick={() => setTerritoire(t)}
                        className={`px-2.5 py-1.5 rounded-lg text-xs font-bold ${territoire === t ? "bg-amber-500 text-slate-900" : "bg-teca-800 text-slate-300"}`}>
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
                <Button data-testid="logout-button" variant="outline" onClick={() => { logout(); navigate("/login"); }}
                  className="w-full border-slate-600 text-slate-200 hover:bg-teca-800 touch-target">
                  <LogOut className="h-4 w-4 mr-2" /> Déconnexion
                </Button>
              </div>
            </SheetContent>
          </Sheet>
          <div className="flex-1 leading-tight flex items-center gap-2.5">
            <img src="/logo-teca.png" alt="TECA Solutions TP" className="h-9 w-auto bg-white rounded-lg px-1.5 py-0.5" data-testid="header-logo" />
            <div>
              <div className="font-display text-lg font-bold uppercase tracking-tight">TECA <span className="text-amber-500">BUILD 360</span></div>
              <div className="text-[10px] text-slate-400 uppercase tracking-widest hidden sm:block">TECA Solutions TP — {territoire}</div>
            </div>
          </div>
          <Button variant="ghost" size="icon" data-testid="theme-toggle" onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            className="text-white hover:bg-teca-800 touch-target">
            {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4 sm:p-6 pb-28 md:pb-8">
        <Outlet />
      </main>

      <button data-testid="quick-new-button" onClick={() => setQuickNew(true)}
        className="fixed bottom-20 md:bottom-8 right-4 md:right-8 z-40 h-16 w-16 rounded-2xl bg-amber-500 text-slate-900 shadow-xl hover:bg-amber-400 flex items-center justify-center transition-transform hover:scale-105 active:scale-95">
        <Plus className="h-8 w-8" strokeWidth={3} />
      </button>
      <QuickNew open={quickNew} onClose={() => setQuickNew(false)} />

      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-teca-900 dark:bg-teca-950 border-t border-slate-700 grid grid-cols-5">
        {[
          { to: "/", icon: LayoutDashboard, l: "Accueil", tid: "tab-home", end: true },
          { to: "/calculs", icon: Calculator, l: "Calculs", tid: "tab-calculs" },
          { to: "/chantiers", icon: HardHat, l: "Chantiers", tid: "tab-chantiers" },
          { to: "/devis", icon: FileText, l: "Devis", tid: "tab-devis" },
          { to: "/crm", icon: Users, l: "CRM", tid: "tab-crm" },
        ].map((t) => (
          <NavLink key={t.to} to={t.to} end={t.end} data-testid={t.tid}
            className={({ isActive }) => `flex flex-col items-center justify-center py-2 text-[10px] font-bold ${isActive ? "text-amber-500" : "text-slate-400"}`}>
            <t.icon className="h-5 w-5 mb-0.5" />{t.l}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
